import { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  DashboardListContentPage,
  TextLoadingPlaceholder,
  SimpleTable,
  InvoiceName,
  QuestionTooltip,
  UnlockCreditsModal,
} from 'components';
import { useTranslation } from 'react-i18next';
import {
  CreditOperation,
  useClearCreditCache,
  useCreditBalance,
  useCreditOperations,
  useCurrentOrganizationId,
  usePendingCreditOperations,
} from '@app/api';
import { Button, Link, useBoolModalState } from '@app/core';
import moment from 'moment-timezone';
import { toEuros } from 'lib/numbers';
import { EmptyCreditsPage } from './EmptyCreditsPage';
import { Amount } from './Cells/Amount';
import { i18n } from '@app/i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  placeholder: {
    ...theme.fonts.h3,
  },
  incoming: {
    ...theme.fonts.h4,

    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  subTitle: {
    ...theme.fonts.h4,
    marginTop: theme.marginBase * 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  expirationDate: {
    ...theme.fonts.body,
  },
  table: {
    marginTop: theme.marginBase * 3,
  },
}));

const useColumns = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        title: t('billing.dashboard.details.table.description'),
        key: (credit: CreditOperation) =>
          credit.sessionId ? (
            <Link to={`/sessions/details/${credit.sessionId}`}>
              {credit.description}
            </Link>
          ) : (
            credit.description
          ),
      },
      {
        title: t('billing.dashboard.details.table.amount'),
        key: ({ amount }: CreditOperation) => <Amount amount={amount} />,
      },
      {
        title: t('billing.dashboard.details.table.invoice'),
        key: ({ invoiceId }: any) => <InvoiceName invoiceId={invoiceId} />,
      },
      {
        title: t('billing.dashboard.details.table.date'),
        key: ({ date }: CreditOperation) =>
          Intl.DateTimeFormat(i18n.language, {
            timeStyle: 'short',
            dateStyle: 'medium',
          }).format(moment(date).toDate()),
      },
      {
        title: t('billing.dashboard.details.table.nextBalance'),
        key: ({ nextBalance }: CreditOperation) =>
          nextBalance ? toEuros(nextBalance) : '-',
      },
    ],
    [t],
  );
};

export const Dashboard = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [unlockOpened, openUnlockCreditsModal, closeUnlockCreditsModal] =
    useBoolModalState();

  const columns = useColumns();

  const organizationId = useCurrentOrganizationId();
  const {
    data: balance,
    isSuccess,
    isLoading: isBalanceLoading,
  } = useCreditBalance(organizationId);

  const { data: creditOperations, isLoading: isUsageLoading } =
    useCreditOperations(organizationId);
  const { data: pendingCreditOperations } =
    usePendingCreditOperations(organizationId);
  const clearCache = useClearCreditCache();

  const incomingAmount = useMemo(() => {
    return (pendingCreditOperations || []).reduce(
      (acc, { amount }) => acc + amount,
      0,
    );
  }, [balance, pendingCreditOperations]);

  const onCreditPurchase = useCallback(() => {
    clearCache();
    closeUnlockCreditsModal();
  }, [closeUnlockCreditsModal, clearCache]);

  const isLoading = isBalanceLoading || isUsageLoading;
  if (!isLoading && !creditOperations?.length) {
    return <EmptyCreditsPage />;
  }

  return (
    <DashboardListContentPage
      title={t('billing.dashboard.titleAmount', {
        amount: balance ? balance.amount : 0,
        context: isBalanceLoading || !isSuccess ? 'loading' : '',
      })}
      titleSideElement={
        isBalanceLoading && (
          <TextLoadingPlaceholder
            sample="5 0000,00 €"
            className={classes.placeholder}
          />
        )
      }
      fetching={isLoading}
      actions={
        <Button
          onClick={openUnlockCreditsModal}
          text={t('billing.dashboard.unlock')}
          color="accent"
        />
      }
    >
      {!!incomingAmount && (
        <h2 className={classes.incoming}>
          {t('billing.dashboard.incomingAmount', { amount: incomingAmount })}
        </h2>
      )}
      <div className={classes.expirationDate}>
        {balance &&
          !!balance.expirationDate &&
          t('billing.dashboard.expirationDate', {
            date: moment(balance.expirationDate).format('LL'),
          })}
      </div>
      {!!pendingCreditOperations?.length && (
        <>
          <h2 className={classes.subTitle}>
            {t('billing.dashboard.pending')}
            <QuestionTooltip title={t('billing.dashboard.pendingTooltip')} />
          </h2>
          <div className={classes.table}>
            <SimpleTable data={pendingCreditOperations} columns={columns} />
          </div>
        </>
      )}
      <h2 className={classes.subTitle}>{t('billing.dashboard.history')}</h2>
      <div className={classes.table}>
        <SimpleTable data={creditOperations} columns={columns} />
      </div>
      <UnlockCreditsModal
        isOpen={unlockOpened}
        onRequestClose={onCreditPurchase}
      />
    </DashboardListContentPage>
  );
};
